/**
 * Phase 1 workflow editor — Section 4 of the design doc.
 *
 * Three-panel layout:
 *  - Header row (status pill, version, active count, Graph/History/Save/Publish)
 *  - Metadata section: name/key/applies-to/description/tags
 *  - Triggers section (workflow-level)
 *  - Two-column body: steps list (left) + Step Inspector (right)
 *
 * Read-only banner for ADMIN_PUBLISHED workflows that the current user
 * can't edit, or peer-published workflows. Banner exposes the copy modal +
 * "view graph" link.
 *
 * Sheets: the graph sheet is wired via `?graph=1` and rendered by a Phase 2
 * agent; here we only swap a placeholder if the param is present so the
 * routing surface is correct. History sheet — same deal: stubbed to an
 * inline panel that lists recent audit rows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type { StepSpec, TransitionSpec, TriggerSpec } from '@mana/shared';
import { api, ApiError } from '../../lib/api.js';
import { useAuth, useModulePermission } from '../../lib/auth-context.js';
import { Button } from '../../ui/Button.js';
import { Badge, Card, CardBody, CardHeader } from '../../ui/Card.js';
import { Field, Input, Textarea } from '../../ui/Input.js';
import { PublishPopover, type PublishChoice } from './PublishPopover.js';
import { UnpublishDialog, type UnpublishChoice } from './UnpublishDialog.js';
import { CopyModal, type CopyChoice } from './CopyModal.js';
import {
  TriggerForm,
  defaultTriggerOfType,
  describeTrigger,
  triggerKindIcon,
} from './TriggerForm.js';
import { ActionChips, StepInspector } from './StepInspector.js';
import type { WorkflowDetail, WorkflowMeta, WorkflowTriggerRow } from './types.js';
import { GraphView } from '../../workflows/GraphView.js';
import { VisualBuilder } from './builder/VisualBuilder.js';

// ---------------------------------------------------------------------------
// Tab toggle — picks "Visual builder" vs "Structured form" view. The user's
// most-recent choice is remembered in localStorage; brand-new workflows
// (zero steps) default to the visual builder per the design brief.
// ---------------------------------------------------------------------------

type BuilderTab = 'visual' | 'structured';
const BUILDER_TAB_KEY = 'mana.workflows.builderTab';

function readSavedTab(): BuilderTab | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(BUILDER_TAB_KEY);
  return raw === 'visual' || raw === 'structured' ? raw : null;
}

/**
 * Pull a human-friendly message out of a save/publish failure. The server
 * returns 400 + a Zod issues array on validation errors; we surface the first
 * issue's path + message so the user can see *what* didn't validate instead
 * of staring at a stuck Save button.
 */
function formatMutationError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as
      | { error?: string; message?: string; issues?: Array<{ path?: unknown[]; message?: string }> }
      | null;
    if (body?.issues && body.issues.length > 0) {
      const first = body.issues[0]!;
      const path = Array.isArray(first.path) ? first.path.join('.') : '';
      return `${body.error ?? 'invalid_body'} — ${path ? path + ': ' : ''}${first.message ?? ''}`;
    }
    return body?.message ?? body?.error ?? err.message ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

function writeSavedTab(tab: BuilderTab): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BUILDER_TAB_KEY, tab);
}

// ---------------------------------------------------------------------------
// Local editable draft type — mirrors ZWorkflowBody shape but without the
// fields that are server-owned (version, status, ids).
// ---------------------------------------------------------------------------

interface WorkflowDraft {
  name: string;
  description: string;
  tags: string[];
  attributionVisible: boolean;
  appliesToProducts: string[];
  appliesToStates: string[];
  triggers: TriggerSpec[];
  steps: StepSpec[];
  transitions: TransitionSpec[];
}

function detailToDraft(detail: WorkflowDetail): WorkflowDraft {
  const triggers: TriggerSpec[] = detail.triggers.map((t) => triggerRowToSpec(t));
  const stepIdToKey = new Map(detail.steps.map((s) => [s.id, s.key]));
  const transitions: TransitionSpec[] = detail.transitions.map((t) => ({
    fromStepKey: t.fromStepId ? stepIdToKey.get(t.fromStepId) ?? null : null,
    toStepKey: stepIdToKey.get(t.toStepId) ?? '',
    label: t.label ?? undefined,
    guardJson: t.guardJson ?? undefined,
  }));
  const steps: StepSpec[] = detail.steps
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      key: s.key,
      name: s.name,
      category: s.category,
      guardJson: s.guardJson ?? undefined,
      actions: s.actions,
      cooldownHours: s.cooldownHours,
      isTerminal: s.isTerminal,
    }));
  return {
    name: detail.workflow.name,
    description: detail.workflow.description ?? '',
    tags: detail.workflow.tags,
    attributionVisible: detail.workflow.attributionVisible,
    appliesToProducts: detail.workflow.appliesToProducts,
    appliesToStates: detail.workflow.appliesToStates,
    triggers,
    steps,
    transitions,
  };
}

function triggerRowToSpec(row: WorkflowTriggerRow): TriggerSpec {
  const cfg = row.config as TriggerSpec;
  if (cfg && (cfg.type === 'time' || cfg.type === 'event' || cfg.type === 'condition')) {
    return cfg;
  }
  // Fallback: synthesize from the row's hot columns.
  if (row.kind === 'TIME') {
    return { type: 'time', mode: 'cron', cron: row.cronExpr ?? '', timezone: 'America/Chicago' };
  }
  if (row.kind === 'EVENT') {
    return { type: 'event', event: 'loan.status_changed' };
  }
  return { type: 'condition', predicate: { op: 'exists', path: 'loan.id' } };
}

// ---------------------------------------------------------------------------
export function WorkflowEditorView() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const auth = useAuth();
  const perm = useModulePermission('workflows');

  const isAdmin =
    auth.me?.user.role === 'admin' || auth.me?.user.role === 'super_admin';

  const detailQuery = useQuery<WorkflowDetail>({
    queryKey: ['workflows', 'detail', id],
    queryFn: () => api<WorkflowDetail>(`/api/v1/workflows/${id}`),
    enabled: !!id,
  });

  // ---- editable draft ----------------------------------------------------
  const [draft, setDraft] = useState<WorkflowDraft | null>(null);
  const [selectedStepKey, setSelectedStepKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [showUnpublish, setShowUnpublish] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  // Tab between the visual (DnD) builder and the structured form. We pick
  // a default lazily once the draft is hydrated — see effect below.
  const [builderTab, setBuilderTab] = useState<BuilderTab | null>(null);

  // Hydrate draft once per loaded workflow.
  useEffect(() => {
    if (!detailQuery.data) return;
    if (lastLoadedIdRef.current === detailQuery.data.workflow.id && draft !== null) return;
    const d = detailToDraft(detailQuery.data);
    setDraft(d);
    setSelectedStepKey(d.steps[0]?.key ?? null);
    setDirty(false);
    lastLoadedIdRef.current = detailQuery.data.workflow.id;
    // Decide the initial tab — use the user's last choice if any, otherwise
    // default to visual for new (empty) workflows and structured otherwise.
    if (builderTab === null) {
      const saved = readSavedTab();
      setBuilderTab(saved ?? (d.steps.length === 0 ? 'visual' : 'structured'));
    }
  }, [detailQuery.data, draft, builderTab]);

  // Persist the tab choice whenever the user flips it.
  useEffect(() => {
    if (builderTab) writeSavedTab(builderTab);
  }, [builderTab]);

  const meta = detailQuery.data?.workflow;
  const isReadOnly = computeReadOnly({ meta, isAdmin, isOwner: meta?.authorId === auth.me?.user.id });

  // ---- mutations ---------------------------------------------------------
  const saveMut = useMutation({
    mutationFn: (patch: Partial<WorkflowDraft>) =>
      api<{ workflow: WorkflowMeta }>(`/api/v1/workflows/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: patch.name,
          description: patch.description ?? null,
          tags: patch.tags,
          attributionVisible: patch.attributionVisible,
          appliesToProducts: patch.appliesToProducts,
          appliesToStates: patch.appliesToStates,
          // Structural fields must travel together — the server's
          // ZPatchBody.superRefine rejects partial structural patches.
          steps: patch.steps,
          transitions: patch.transitions,
          triggers: patch.triggers,
        }),
      }),
    onSuccess: () => {
      // Structural fields just changed on the server; invalidate the detail
      // query so the next render re-hydrates from the canonical row.
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['workflows', 'detail', id] });
      setDirty(false);
    },
  });

  const publishMut = useMutation({
    // /:id/publish flips DRAFT→ACTIVE against whatever is already persisted,
    // so we MUST PATCH the current draft first — otherwise structural edits
    // the user just made (steps, transitions, triggers) silently don't ship
    // when they click Publish without first clicking Save.
    mutationFn: async (choice: PublishChoice) => {
      if (!draft) throw new Error('draft_not_loaded');
      await api(`/api/v1/workflows/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          attributionVisible: choice.attributionVisible,
          name: draft.name,
          description: draft.description ?? null,
          tags: draft.tags,
          appliesToProducts: draft.appliesToProducts,
          appliesToStates: draft.appliesToStates,
          steps: draft.steps,
          transitions: draft.transitions,
          triggers: draft.triggers,
        }),
      });
      return api<{ workflow: WorkflowMeta }>(`/api/v1/workflows/${id}/publish`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['workflows', 'detail', id] });
      setDirty(false);
    },
  });

  const unpublishMut = useMutation({
    mutationFn: (choice: UnpublishChoice) =>
      api<{ workflow: WorkflowMeta }>(`/api/v1/workflows/${id}/unpublish`, {
        method: 'POST',
        body: JSON.stringify(choice),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      setShowUnpublish(false);
    },
  });

  const copyMut = useMutation({
    mutationFn: (choice: CopyChoice) =>
      api<{ workflow: WorkflowMeta }>(`/api/v1/workflows/${id}/copy`, {
        method: 'POST',
        body: JSON.stringify(choice),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      setShowCopy(false);
      navigate(`/workflows/${data.workflow.id}`);
    },
  });

  // ---- derived ------------------------------------------------------------
  const selectedStepIndex = useMemo(() => {
    if (!draft || !selectedStepKey) return -1;
    return draft.steps.findIndex((s) => s.key === selectedStepKey);
  }, [draft, selectedStepKey]);

  const selectedStep = selectedStepIndex >= 0 && draft ? draft.steps[selectedStepIndex] : null;

  // ----- handlers ---------------------------------------------------------
  const patchDraft = useCallback((updater: (d: WorkflowDraft) => WorkflowDraft) => {
    setDraft((cur) => (cur ? updater(cur) : cur));
    setDirty(true);
  }, []);

  const patchStep = useCallback(
    (idx: number, next: StepSpec) => {
      patchDraft((d) => {
        const oldKey = d.steps[idx]!.key;
        const newKey = next.key;
        const steps = d.steps.map((s, i) => (i === idx ? next : s));
        // If key changed, fix referencing transitions (rare in Phase 1 — keys are append-only post-publish but draft renames are allowed).
        let transitions = d.transitions;
        if (oldKey !== newKey) {
          transitions = d.transitions.map((t) => ({
            ...t,
            fromStepKey: t.fromStepKey === oldKey ? newKey : t.fromStepKey,
            toStepKey: t.toStepKey === oldKey ? newKey : t.toStepKey,
          }));
        }
        return { ...d, steps, transitions };
      });
    },
    [patchDraft],
  );

  const moveStep = useCallback(
    (idx: number, dir: -1 | 1) => {
      patchDraft((d) => {
        const j = idx + dir;
        if (j < 0 || j >= d.steps.length) return d;
        const steps = [...d.steps];
        const tmp = steps[idx]!;
        steps[idx] = steps[j]!;
        steps[j] = tmp;
        return { ...d, steps };
      });
    },
    [patchDraft],
  );

  const addStep = useCallback(() => {
    patchDraft((d) => {
      const nextIdx = d.steps.length + 1;
      const key = `STEP_${nextIdx}`;
      const step: StepSpec = {
        key,
        name: `Step ${nextIdx}`,
        category: 'NOTIFY',
        actions: [],
        cooldownHours: 0,
        isTerminal: false,
      };
      return { ...d, steps: [...d.steps, step] };
    });
  }, [patchDraft]);

  const removeStep = useCallback(
    (idx: number) => {
      patchDraft((d) => {
        const removedKey = d.steps[idx]!.key;
        const steps = d.steps.filter((_, i) => i !== idx);
        const transitions = d.transitions.filter(
          (t) => t.fromStepKey !== removedKey && t.toStepKey !== removedKey,
        );
        return { ...d, steps, transitions };
      });
      if (selectedStepKey === draft?.steps[idx]?.key) {
        setSelectedStepKey(draft?.steps[idx === 0 ? 1 : 0]?.key ?? null);
      }
    },
    [patchDraft, selectedStepKey, draft],
  );

  // Branched graph detection — disables reorder per H3.
  const isLinear = useMemo(() => {
    if (!draft) return true;
    if (draft.transitions.length === 0) return true;
    // A linear workflow has at most one outgoing per step that goes to the
    // very next step in the list (or empty / null-from entry edges).
    const stepIdxByKey = new Map(draft.steps.map((s, i) => [s.key, i]));
    for (const t of draft.transitions) {
      if (t.fromStepKey === null) continue;
      const fromIdx = stepIdxByKey.get(t.fromStepKey);
      const toIdx = stepIdxByKey.get(t.toStepKey);
      if (fromIdx === undefined || toIdx === undefined) return false;
      if (toIdx !== fromIdx + 1) return false;
    }
    return true;
  }, [draft]);

  // ---- guards on render --------------------------------------------------
  if (!id) {
    return <div className="text-sm text-slate-500">Missing workflow id.</div>;
  }
  if (detailQuery.isLoading || !draft || !meta) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="surface-card p-6">
        <h3 className="font-display text-lg text-rose-700">Couldn&apos;t load workflow</h3>
        <p className="mt-2 text-sm text-slate-600">
          {(detailQuery.error as Error)?.message ?? 'Unknown error'}
        </p>
        <Button variant="secondary" className="mt-3" onClick={() => navigate('/workflows')}>
          ← Back to list
        </Button>
      </div>
    );
  }

  const showGraphParam = searchParams.get('graph') === '1';

  // ---- render ------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* HEADER ROW -------------------------------------------------------- */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
            ← Workflows
          </Button>
          <span className="text-slate-300">/</span>
          <h1 className="font-display text-xl font-bold text-brand-navy-800">{draft.name}</h1>
          <StatusPill status={meta.status} />
          <Badge tone="neutral">v{meta.version}</Badge>
          <span className="text-xs text-slate-500">
            {meta.activeInstanceCount} active loan{meta.activeInstanceCount === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchParams({ graph: '1' })}
            title="Open the graph viewer (Phase 2)"
          >
            Graph
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory((s) => !s)}
          >
            History
          </Button>
          {!isReadOnly && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!dirty || saveMut.isPending}
              onClick={() =>
                saveMut.mutate({
                  name: draft.name,
                  description: draft.description,
                  tags: draft.tags,
                  attributionVisible: draft.attributionVisible,
                  appliesToProducts: draft.appliesToProducts,
                  appliesToStates: draft.appliesToStates,
                  steps: draft.steps,
                  transitions: draft.transitions,
                  triggers: draft.triggers,
                })
              }
            >
              {saveMut.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </Button>
          )}
          {!isReadOnly && meta.status === 'DRAFT' && (
            <PublishPopover
              currentVisibility={meta.visibility}
              currentAttribution={meta.attributionVisible}
              isAdmin={isAdmin}
              isPending={publishMut.isPending}
              disabled={!perm.canEdit}
              onPublish={(c) => publishMut.mutate(c)}
            />
          )}
          {!isReadOnly && meta.status === 'ACTIVE' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowUnpublish(true)}
            >
              Unpublish ▾
            </Button>
          )}
        </div>
      </header>

      {/* SAVE / PUBLISH ERROR BANNER — surfaces validation failures the user
          would otherwise see only as a stuck "Save" button. */}
      {(saveMut.isError || publishMut.isError) && (
        <div
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
        >
          <strong className="font-semibold">
            {saveMut.isError ? 'Save failed: ' : 'Publish failed: '}
          </strong>
          {formatMutationError(saveMut.error ?? publishMut.error)}
          <button
            type="button"
            onClick={() => {
              saveMut.reset();
              publishMut.reset();
            }}
            className="ml-3 underline hover:text-rose-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* GRAPH SHEET — right-side drawer rendered as a fixed overlay --- */}
      {showGraphParam && (
        <GraphSheet workflowId={id} onClose={() => setSearchParams({})} />
      )}

      {/* READ-ONLY BANNER ------------------------------------------------- */}
      {isReadOnly && (
        <Card className="border-amber-200 bg-amber-50">
          <CardBody className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              🔒 Read-only — {meta.visibility === 'ADMIN_PUBLISHED' ? 'published by Admin' : 'shared by a peer'}
              {' · '}v{meta.version}
              {meta.publishedAt && (
                <span> · last published {new Date(meta.publishedAt).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowCopy(true)}>
                Make my own copy
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams({ graph: '1' })}
              >
                View graph
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* METADATA --------------------------------------------------------- */}
      <Card>
        <CardHeader title="Metadata" />
        <CardBody className="grid grid-cols-2 gap-4">
          <Field label="Name">
            <Input
              value={draft.name}
              onChange={(e) => {
                const value = e.target.value;
                patchDraft((d) => ({ ...d, name: value }));
              }}
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Key" hint="Auto-generated from name; immutable after publish.">
            <Input value={meta.key} readOnly disabled />
          </Field>
          <Field label="Applies to (products)">
            <TagListInput
              values={draft.appliesToProducts}
              onChange={(v) => patchDraft((d) => ({ ...d, appliesToProducts: v }))}
              placeholder="conventional, va, fha…"
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Tags">
            <TagListInput
              values={draft.tags}
              onChange={(v) => patchDraft((d) => ({ ...d, tags: v }))}
              placeholder="post-close, welcome…"
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Description">
            <Textarea
              rows={2}
              value={draft.description}
              onChange={(e) => {
                const value = e.target.value;
                patchDraft((d) => ({ ...d, description: value }));
              }}
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Applies to (lifecycle states)">
            <TagListInput
              values={draft.appliesToStates}
              onChange={(v) => patchDraft((d) => ({ ...d, appliesToStates: v }))}
              placeholder="Underwriting, Closing…"
              disabled={isReadOnly}
            />
          </Field>
        </CardBody>
      </Card>

      {/* BUILDER TAB TOGGLE ------------------------------------------------ */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Editor mode
        </span>
        <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white">
          {(['visual', 'structured'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setBuilderTab(tab)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold transition-colors',
                builderTab === tab
                  ? 'bg-brand-navy-700 text-white'
                  : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {tab === 'visual' ? 'Visual builder' : 'Structured form'}
            </button>
          ))}
        </div>
        <span className="ml-2 text-[11px] text-slate-500">
          {builderTab === 'visual'
            ? 'Drag fields onto a condition, drop actions to send notifications.'
            : 'Edit the structured form view of the workflow.'}
        </span>
      </div>

      {/* VISUAL BUILDER --------------------------------------------------- */}
      {builderTab === 'visual' && (
        <VisualBuilder
          steps={draft.steps}
          triggers={draft.triggers}
          readOnly={isReadOnly}
          onChange={(next) => {
            // The visual builder hands back a fully-formed steps + triggers
            // pair. Bump the dirty flag and replace both arrays. Transitions
            // remain unchanged — the visual builder is step-and-trigger only
            // for Phase 1, so we don't have to reconcile branch edges here.
            patchDraft((d) => ({
              ...d,
              steps: next.steps,
              triggers: next.triggers,
            }));
          }}
        />
      )}

      {/* STRUCTURED-FORM TRIGGERS ----------------------------------------- */}
      {builderTab === 'structured' && (
      <>
      <Card>
        <CardHeader
          title={`Triggers (${draft.triggers.length})`}
          actions={
            !isReadOnly ? (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    patchDraft((d) => ({
                      ...d,
                      triggers: [...d.triggers, defaultTriggerOfType('time')],
                    }))
                  }
                >
                  + Time
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    patchDraft((d) => ({
                      ...d,
                      triggers: [...d.triggers, defaultTriggerOfType('event')],
                    }))
                  }
                >
                  + Event
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    patchDraft((d) => ({
                      ...d,
                      triggers: [...d.triggers, defaultTriggerOfType('condition')],
                    }))
                  }
                >
                  + Condition
                </Button>
              </div>
            ) : undefined
          }
        />
        <CardBody className="space-y-3">
          {draft.triggers.length === 0 && (
            <p className="text-xs text-slate-500">No triggers — workflow will never fire.</p>
          )}
          {draft.triggers.map((trig, idx) => (
            <div key={idx} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2 pb-2">
                <span className="text-sm font-medium text-brand-navy-800">
                  {triggerKindIcon(trig.type)} {describeTrigger(trig)}
                </span>
                {!isReadOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      patchDraft((d) => ({
                        ...d,
                        triggers: d.triggers.filter((_, i) => i !== idx),
                      }))
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
              <TriggerForm
                trigger={trig}
                availableStepKeys={draft.steps.map((s) => s.key)}
                onChange={(next) =>
                  patchDraft((d) => ({
                    ...d,
                    triggers: d.triggers.map((t, i) => (i === idx ? next : t)),
                  }))
                }
              />
            </div>
          ))}
        </CardBody>
      </Card>

      {/* STEPS + INSPECTOR ---------------------------------------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader
            title={`Steps (${draft.steps.length})`}
            subtitle={
              isLinear
                ? 'Reorder with ▲ ▼.'
                : 'Branched workflow — use Add branch to reroute. Reorder disabled.'
            }
            actions={
              !isReadOnly ? (
                <Button size="sm" variant="ghost" onClick={addStep}>
                  + Add step
                </Button>
              ) : undefined
            }
          />
          <CardBody className="space-y-1.5">
            {draft.steps.map((step, idx) => {
              const isSelected = selectedStepKey === step.key;
              return (
                <div
                  key={step.key}
                  onClick={() => setSelectedStepKey(step.key)}
                  className={clsx(
                    'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors',
                    isSelected
                      ? 'border-brand-navy-300 bg-brand-navy-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50',
                  )}
                >
                  <span className="w-6 text-xs font-semibold text-slate-400">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-brand-navy-800">
                      {step.name || '(unnamed)'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <Badge tone="neutral">{step.category}</Badge>
                      <ActionChips actions={step.actions} />
                    </div>
                  </div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        disabled={!isLinear || idx === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveStep(idx, -1);
                        }}
                        title={isLinear ? 'Move up' : 'Reorder disabled for branched workflows'}
                        className="rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={!isLinear || idx === draft.steps.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveStep(idx, 1);
                        }}
                        title={isLinear ? 'Move down' : 'Reorder disabled for branched workflows'}
                        className="rounded px-1 py-0.5 text-xs text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStep(idx);
                        }}
                        title="Remove step"
                        className="rounded px-1 py-0.5 text-xs text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Step inspector" />
          <CardBody>
            {selectedStep ? (
              <StepInspector
                step={selectedStep}
                stepIndex={selectedStepIndex}
                totalSteps={draft.steps.length}
                availableNextStepKeys={draft.steps
                  .filter((s) => s.key !== selectedStep.key)
                  .map((s) => s.key)}
                outgoingTransitions={draft.transitions.filter(
                  (t) => t.fromStepKey === selectedStep.key,
                )}
                readOnly={isReadOnly}
                onPatch={(next) => patchStep(selectedStepIndex, next)}
                onPatchTransitions={(nextOutgoing) =>
                  patchDraft((d) => {
                    const others = d.transitions.filter(
                      (t) => t.fromStepKey !== selectedStep.key,
                    );
                    return { ...d, transitions: [...others, ...nextOutgoing] };
                  })
                }
              />
            ) : (
              <p className="text-xs text-slate-500">Select a step to edit its details.</p>
            )}
          </CardBody>
        </Card>
      </div>
      </>
      )}

      {/* HISTORY SHEET (lightweight inline panel) ------------------------ */}
      {showHistory && (
        <HistoryPanel workflowId={id} onClose={() => setShowHistory(false)} />
      )}

      {/* DIALOGS --------------------------------------------------------- */}
      <CopyModal
        open={showCopy}
        sourceName={meta.name}
        isPending={copyMut.isPending}
        onClose={() => setShowCopy(false)}
        onSubmit={(c) => copyMut.mutate(c)}
      />
      <UnpublishDialog
        open={showUnpublish}
        isAdmin={isAdmin}
        activeInstanceCount={meta.activeInstanceCount}
        isPending={unpublishMut.isPending}
        onClose={() => setShowUnpublish(false)}
        onSubmit={(c) => unpublishMut.mutate(c)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
function StatusPill({ status }: { status: WorkflowMeta['status'] }) {
  if (status === 'DRAFT') return <Badge tone="neutral">Draft</Badge>;
  if (status === 'ACTIVE') return <Badge tone="success">Active</Badge>;
  return <Badge tone="warning">Archived</Badge>;
}

function computeReadOnly({
  meta,
  isAdmin,
  isOwner,
}: {
  meta: WorkflowMeta | undefined;
  isAdmin: boolean;
  isOwner: boolean;
}): boolean {
  if (!meta) return true;
  // ADMIN_PUBLISHED: anyone non-admin sees it read-only.
  if (meta.visibility === 'ADMIN_PUBLISHED' && !isAdmin) return true;
  // ORG_PUBLISHED by another author: read-only for viewers other than the author/admin.
  if (meta.visibility === 'ORG_PUBLISHED' && !isOwner && !isAdmin) return true;
  return false;
}

function TagListInput({
  values,
  onChange,
  placeholder,
  disabled,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 shadow-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-brand-navy-50 px-2 py-0.5 text-[11px] font-medium text-brand-navy-700 ring-1 ring-brand-navy-200"
          >
            {v}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="text-brand-navy-400 hover:text-brand-navy-700"
                title={`Remove ${v}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
              e.preventDefault();
              const v = draft.trim().replace(/,$/, '');
              if (!values.includes(v)) onChange([...values, v]);
              setDraft('');
            }
            if (e.key === 'Backspace' && draft === '' && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
          placeholder={values.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[6rem] border-0 bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
interface AuditRow {
  id: string;
  action: string;
  actorUserId: string | null;
  createdAt: string;
  beforeJson: string | null;
  afterJson: string | null;
}

function HistoryPanel({ workflowId, onClose }: { workflowId: string; onClose: () => void }) {
  const auditQuery = useQuery<{ items: AuditRow[]; nextCursor: string | null }>({
    queryKey: ['workflows', 'audit', workflowId],
    queryFn: () => api(`/api/v1/workflows/${workflowId}/audit?limit=25`),
  });

  return (
    <Card>
      <CardHeader
        title="History"
        subtitle="Recent edits, publishes, and runs."
        actions={
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        }
      />
      <CardBody className="space-y-1.5">
        {auditQuery.isLoading && (
          <p className="text-xs text-slate-500">Loading…</p>
        )}
        {auditQuery.data?.items.length === 0 && (
          <p className="text-xs text-slate-500">No activity yet.</p>
        )}
        {auditQuery.data?.items.map((row) => (
          <div
            key={row.id}
            className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-2 py-1.5 text-xs"
          >
            <span className="font-mono text-brand-navy-700">{row.action}</span>
            <span className="text-slate-500">
              {new Date(row.createdAt).toLocaleString()}
            </span>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Right-side drawer that hosts the read-only React Flow graph viewer.
// Rendered as a fixed overlay so it floats above the editor without
// affecting the parent layout. Allows opening even when the workflow has
// zero steps — the GraphView component handles its own empty state.
// ---------------------------------------------------------------------------

function GraphSheet({
  workflowId,
  onClose,
}: {
  workflowId: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      {/* backdrop ----------------------------------------------------- */}
      <button
        type="button"
        aria-label="Close graph viewer"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-slate-900/30"
      />
      {/* drawer ------------------------------------------------------- */}
      <div className="relative ml-auto flex h-full w-full max-w-[min(1100px,95vw)] flex-col border-l border-slate-200 bg-white shadow-floating">
        <GraphView workflowId={workflowId} onClose={onClose} />
      </div>
    </div>
  );
}
